import { useState } from "react";
import {
  DEFAULT_AVATAR,
  FACES,
  FOODS,
  HAIR_COLORS,
  HAIR_STYLES,
  HATS,
  ITEMS,
  OCCUPATIONS,
  SHIRTS,
  SKINS,
  type Avatar,
} from "../lib/avatar";
import { saveUserProfile, type UserProfile } from "../lib/firebase";
import { PixelAvatar } from "./PixelAvatar";

type Draft = Omit<UserProfile, "completed">;

/** Nickname + AKA + 8-bit character + 3 optional one-tap questions + leaderboard switch.
 *  Used by the one-time welcome page and by the profile page. */
export function ProfileForm({
  userId,
  initial,
  submitLabel,
  onSaved,
}: {
  userId: string;
  initial: UserProfile;
  submitLabel: string;
  onSaved: () => void;
}) {
  const [d, setD] = useState<Draft>(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  type Part = Exclude<keyof Avatar, "person">;
  const setAvatar = (k: Part, v: number) => setD({ ...d, avatar: { ...d.avatar, [k]: v } });
  const setPerson = (person: boolean) => setD({ ...d, avatar: { ...d.avatar, person } });

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!d.nickname.trim()) return setError("ใส่ชื่อเล่นก่อนนะครับ");
    if (!d.occupation || !d.goal.trim() || !d.question.trim()) return setError("ตอบ 3 ข้อด้านล่างก่อนนะครับ");
    setSaving(true);
    setError(null);
    try {
      await saveUserProfile(userId, d);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSaving(false);
    }
  }

  // Food mascots show as tiny previews: a picture picks faster than a name.
  const foodPicker = (
    <div className="pf-row" role="radiogroup" aria-label="เลือกอาหาร">
      <span className="pf-row__label">อาหาร</span>
      {FOODS.map((name, i) => (
        <button
          key={name}
          type="button"
          role="radio"
          aria-checked={d.avatar.food === i}
          title={name}
          className={`pf-food ${d.avatar.food === i ? "is-on" : ""}`}
          onClick={() => setAvatar("food", i)}
        >
          <PixelAvatar avatar={{ ...DEFAULT_AVATAR, food: i }} size={40} />
          <span>{name}</span>
        </button>
      ))}
    </div>
  );
  const swatches = (k: Part, colours: string[], label: string) => (
    <div className="pf-row" role="radiogroup" aria-label={label}>
      <span className="pf-row__label">{label}</span>
      {colours.map((c, i) => (
        <button key={c} type="button" role="radio" aria-checked={d.avatar[k] === i} aria-label={`${label} ${i + 1}`}
          className={`pf-swatch ${d.avatar[k] === i ? "is-on" : ""}`} style={{ background: c }} onClick={() => setAvatar(k, i)} />
      ))}
    </div>
  );
  const choices = (k: Part, names: string[], label: string) => (
    <div className="pf-row" role="radiogroup" aria-label={label}>
      <span className="pf-row__label">{label}</span>
      {names.map((n, i) => (
        <button key={n} type="button" role="radio" aria-checked={d.avatar[k] === i}
          className={`cm-chip ${d.avatar[k] === i ? "cm-chip--on" : ""}`} onClick={() => setAvatar(k, i)}>
          {n}
        </button>
      ))}
    </div>
  );
  const textAnswer = (field: "goal" | "question", label: string, hint: string, placeholder: string) => (
    <label className="pf-q">
      {label}*
      <small className="pf-hint">{hint}</small>
      <textarea rows={2} maxLength={500} value={d[field]} placeholder={placeholder}
        onChange={(e) => { setD({ ...d, [field]: e.target.value }); setError(null); }} />
    </label>
  );

  return (
    <form className="pf" onSubmit={submit}>
      <section className="pf-card pf-builder">
        <div className="pf-preview">
          <PixelAvatar avatar={d.avatar} size={176} />
          <strong>{d.aka.trim() || d.nickname.trim() || "ชื่อของคุณ"}</strong>
        </div>
        <div className="pf-parts">
          <h2>เลือกตัวละครของคุณ</h2>
          <div className="pf-row" role="radiogroup" aria-label="แบบตัวละคร">
            <span className="pf-row__label">แบบ</span>
            {[["อาหาร", false], ["คน", true]].map(([label, person]) => (
              <button key={String(label)} type="button" role="radio" aria-checked={d.avatar.person === person}
                className={`cm-chip ${d.avatar.person === person ? "cm-chip--on" : ""}`} onClick={() => setPerson(person as boolean)}>
                {label}
              </button>
            ))}
          </div>
          {d.avatar.person ? (
            <>
              {swatches("skin", SKINS, "สีผิว")}
              {choices("hair", HAIR_STYLES, "ทรงผม")}
              {swatches("hairColor", HAIR_COLORS, "สีผม")}
              {swatches("shirt", SHIRTS, "สีเสื้อ")}
            </>
          ) : (
            <>
              {foodPicker}
              {choices("face", FACES, "หน้า")}
              {choices("hat", HATS, "หมวก")}
            </>
          )}
          {choices("item", ITEMS, "ของถือ")}
        </div>
      </section>

      <section className="pf-card">
        <label>
          ชื่อเล่น*
          <input value={d.nickname} maxLength={40} placeholder="เช่น ดิว"
            onChange={(e) => { setD({ ...d, nickname: e.target.value }); setError(null); }} />
        </label>
        <label>
          AKA
          <input value={d.aka} maxLength={40} placeholder="เช่น สายหมี AI" onChange={(e) => setD({ ...d, aka: e.target.value })} />
          <small className="pf-hint">
            AKA ย่อมาจาก Also Known As คือฉายาหรือชื่อที่อยากให้เพื่อนในคอมมูนิตี้เห็น
            จะขึ้นใน Chat โลกและลีดเดอร์บอร์ด ถ้าไม่กรอกจะใช้ชื่อเล่นแทน
          </small>
        </label>
        <label className="checkbox">
          <input type="checkbox" checked={d.showOnLeaderboard} onChange={(e) => setD({ ...d, showOnLeaderboard: e.target.checked })} />
          แสดงฉันในลีดเดอร์บอร์ด
        </label>
      </section>

      <section className="pf-card">
        <h2>ขอบคุณที่มาเรียนด้วยกันนะครับ :)</h2>
        <p className="pf-hint">ข้อมูลนี้ช่วยให้ดิวทำบทเรียนและคอนเทนต์ที่ตรงกับคุณที่สุด</p>
        <fieldset className="pf-q">
          <legend>ตอนนี้ทำอะไรอยู่*</legend>
          <div className="pf-q__list" role="radiogroup">
            {OCCUPATIONS.map((o) => (
              <button key={o} type="button" role="radio" aria-checked={d.occupation === o}
                className={`pf-q__option ${d.occupation === o ? "is-on" : ""}`}
                onClick={() => { setD({ ...d, occupation: o }); setError(null); }}>
                {o}
                <span aria-hidden>{d.occupation === o ? "●" : "○"}</span>
              </button>
            ))}
          </div>
        </fieldset>
        {textAnswer("goal", "เป้าหมายในการเรียน AI ของคุณคืออะไร",
          "เอาไปทำธุรกิจตัวเอง? ใช้ในงานประจำ? หารายได้เสริม? เล่าให้ฟังหน่อย", "เช่น อยากใช้ AI ทำคอนเทนต์ขายของให้ร้านตัวเอง")}
        {textAnswer("question", "คำถามเรื่อง AI ที่กำลังสงสัย / อยากรู้มากที่สุด",
          "อะไรที่อยากรู้ เขียนมาได้เลย", "เช่น ทำคลิป AI ยังไงให้ดูไม่ปลอม")}
      </section>

      {error && <p className="error-inline">{error}</p>}
      <button type="submit" className="btn-primary pf-submit" disabled={saving}>{saving ? "กำลังบันทึก…" : submitLabel}</button>
    </form>
  );
}
